import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./ThemeProvider";

// =====================================================================
// Theme-aware palettes — shared by all wizard steps so colours stay
// vivid in dark mode and tasteful in light mode.
//
// NOTE: This codebase has an inverted naming convention in CSS:
// `[data-theme="light"]` rules actually apply the DARK visual theme
// (white text + dark backgrounds), and the default state (no override)
// is the LIGHT visual theme. Consumers must therefore pass
// `isVisuallyDark` (computed as `!useTheme().isDarkMode`) when they
// want truly dark-mode-friendly palettes.
// =====================================================================
function getStatusPalette(isVisuallyDark) {
  return {
    success: isVisuallyDark
      ? {
          accent: "hsl(150 75% 68%)",
          accentStrong: "hsl(150 75% 75%)",
          bg: "linear-gradient(135deg, hsl(150 65% 40% / 0.32), hsl(150 65% 35% / 0.12))",
          border: "1px solid hsl(150 70% 55% / 0.5)",
          iconBg: "hsl(150 70% 50% / 0.28)",
          iconColor: "hsl(150 75% 75%)",
        }
      : {
          accent: "hsl(150 60% 32%)",
          accentStrong: "hsl(150 60% 26%)",
          bg: "linear-gradient(135deg, hsl(150 60% 45% / 0.16), hsl(150 60% 45% / 0.04))",
          border: "1px solid hsl(150 60% 45% / 0.4)",
          iconBg: "hsl(150 60% 45% / 0.22)",
          iconColor: "hsl(150 60% 30%)",
        },
    danger: isVisuallyDark
      ? { accent: "hsl(0 85% 72%)" }
      : { accent: "hsl(0 80% 45%)" },
    surface: isVisuallyDark
      ? {
          bg: "hsl(234 30% 18%)",
          border: "1px solid hsl(234 20% 28%)",
        }
      : {
          bg: "hsl(210 25% 97%)",
          border: "1px solid hsl(220 14% 90%)",
        },
    surfaceMuted: isVisuallyDark
      ? "hsl(234 25% 14%)"
      : "hsl(210 14% 96%)",
  };
}

function getPasswordStrengthColors(isVisuallyDark) {
  return isVisuallyDark
    ? [
        "hsl(0 85% 70%)",
        "hsl(38 95% 65%)",
        "hsl(150 75% 60%)",
        "hsl(166 95% 55%)",
      ]
    : [
        "hsl(0 80% 50%)",
        "hsl(38 92% 50%)",
        "hsl(150 60% 38%)",
        "hsl(166 100% 41%)",
      ];
}

const STEPS = [
  { id: 1, key: "referral" },
  { id: 2, key: "package" },
  { id: 3, key: "organization" },
  { id: 4, key: "review" },
];

const FAKE_PARTNER_DIRECTORY = {
  "NOMIO-0042-AM": "Արամ Մինասյան",
  "NOMIO-0015-AM": "Անի Բաղդասարյան",
  "NOMIO-0007-AM": "Դավիթ Հովհաննիսյան",
};

// Light heuristic so the "Build my package" / Custom button doesn't break.
function getPackageOptions(t) {
  return ["basic", "business", "enterprise"].map((key) => ({
    key,
    name: t(`clientSetup.wizard.step2.packages.${key}.name`),
    price: t(`clientSetup.wizard.step2.packages.${key}.price`, {
      returnObjects: true,
    }),
    features: t(`clientSetup.wizard.step2.packages.${key}.features`, {
      returnObjects: true,
    }),
  }));
}

function ClientPlatformSetupWizard({
  initialContext = {},
  onClose,
}) {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  // The CSS [data-theme="light"] rule is mis-named — it actually applies
  // the dark visual theme. The provider's `isDarkMode` flag therefore is
  // inverted relative to what users see. We compute the true visual flag
  // here so every wizard step can pick the right palette.
  const isVisuallyDark = !isDarkMode;
  const lang = i18n.language || "hy";
  const numLocale = lang === "hy" ? "hy-AM" : "en-US";
  const palette = useMemo(
    () => getStatusPalette(isVisuallyDark),
    [isVisuallyDark],
  );

  // Wizard state
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({
    referral: {
      code: initialContext.attachedReferralCode || "",
      partner: initialContext.partnerName || "",
      source: initialContext.cameFromReferralLink
        ? "link"
        : initialContext.attachedReferralCode
        ? "saved"
        : "none",
    },
    packageKey: "business",
    organization: {
      name: "",
      type: "",
      adminFirstName: initialContext.firstName || "",
      adminLastName: initialContext.lastName || "",
      adminUsername: "",
      password: "",
      passwordConfirm: "",
    },
  });

  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(5);
  const rootRef = useRef(null);
  const scrollRef = useRef(null);

  // When the active step changes, scroll the wizard view back to the top so
  // the user always lands on the new step's header instead of mid-form
  // (this matters most for Step 3 -> Step 4 because Step 3 is long).
  useEffect(() => {
    // Always reset the internal scrollable step container first.
    if (scrollRef.current) {
      try {
        scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        scrollRef.current.scrollTop = 0;
      }
    }

    if (!rootRef.current) return;
    // Also reset every potential modal scroll container — different screen
    // sizes use different scroll viewports in this project's CSS:
    //   * desktop: `.modal-block` (the fixed full-screen overlay)
    //   * mobile:  `.modal-content` (the inner card)
    //   * fallback: walk up looking for any scrolled ancestor.
    const candidates = new Set();
    [
      rootRef.current.closest(".modal-body"),
      rootRef.current.closest(".modal-content"),
      rootRef.current.closest(".modal-content-wrapper"),
      rootRef.current.closest(".modal-block"),
    ].forEach((el) => el && candidates.add(el));

    let cur = rootRef.current.parentElement;
    while (cur && cur !== document.body) {
      if (cur.scrollTop > 0) candidates.add(cur);
      cur = cur.parentElement;
    }

    candidates.forEach((el) => {
      try {
        el.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        el.scrollTop = 0;
      }
    });
  }, [stepIdx, success]);

  const packages = useMemo(() => getPackageOptions(t), [t]);

  const updateData = (patch) =>
    setData((prev) => ({
      ...prev,
      ...patch,
      organization: { ...prev.organization, ...(patch.organization || {}) },
      referral: { ...prev.referral, ...(patch.referral || {}) },
    }));

  const goNext = () =>
    setStepIdx((i) => Math.min(i + 1, STEPS.length - 1));
  const goBack = () => setStepIdx((i) => Math.max(i - 1, 0));
  const goToStep = (idx) => setStepIdx(idx);

  const handleSubmit = () => {
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setSuccess(true);
    }, 1200);
  };

  // Redirect countdown after success
  useEffect(() => {
    if (!success) return;
    if (secondsLeft <= 0) {
      // In a real app: window.location.href = `https://dashboard.nomio.am/sso?token=${oneTimeToken}`;
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [success, secondsLeft]);

  const currentStep = STEPS[stepIdx];

  // Step validity (loose for the demo)
  const isStep3Valid =
    data.organization.name.trim().length > 1 &&
    data.organization.type &&
    data.organization.adminUsername.trim().length > 1 &&
    data.organization.password.length >= 8 &&
    data.organization.password === data.organization.passwordConfirm;

  return (
    <div
      ref={rootRef}
      className="d-flex flex-column"
      style={{
        // Wizard area becomes a bounded flex column so the inner step area
        // scrolls while the progress bar (top) and Back/Next footer (bottom)
        // stay pinned and always reachable.
        maxHeight: "calc(100vh - 220px)",
        gap: "1.25rem",
      }}
    >
      <div style={{ flexShrink: 0 }}>
        <ProgressBar
          steps={STEPS}
          currentIdx={stepIdx}
          success={success}
          t={t}
        />
      </div>

      <style>{`
        .client-wizard-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .client-wizard-scroll::-webkit-scrollbar-track {
          background: transparent;
          margin-block: 4px;
        }
        .client-wizard-scroll::-webkit-scrollbar-thumb {
          background: hsl(var(--main) / 0.28);
          border-radius: 999px;
          border: 2px solid transparent;
          background-clip: padding-box;
          transition: background 0.2s ease;
        }
        .client-wizard-scroll::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--main) / 0.55);
          background-clip: padding-box;
        }
        .client-wizard-scroll {
          scrollbar-width: thin;
          scrollbar-color: hsl(var(--main) / 0.4) transparent;
        }
      `}</style>

      <div
        ref={scrollRef}
        className="d-flex flex-column client-wizard-scroll"
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          gap: "1rem",
          paddingRight: 4,
        }}
      >
        {success ? (
          <SuccessScreen
            secondsLeft={secondsLeft}
            onClose={onClose}
            t={t}
          />
        ) : (
          <>
            {currentStep.id === 1 && (
              <Step1Referral
                data={data.referral}
                onChange={(patch) => updateData({ referral: patch })}
                palette={palette}
                isVisuallyDark={isVisuallyDark}
                t={t}
              />
            )}

            {currentStep.id === 2 && (
              <Step2Package
                data={data.packageKey}
                packages={packages}
                recommendedKey={
                  data.referral.source !== "none" ? "business" : null
                }
                numLocale={numLocale}
                palette={palette}
                onChange={(key) => updateData({ packageKey: key })}
                t={t}
              />
            )}

            {currentStep.id === 3 && (
              <Step3Organization
                data={data.organization}
                onChange={(patch) => updateData({ organization: patch })}
                palette={palette}
                isVisuallyDark={isVisuallyDark}
                t={t}
              />
            )}

            {currentStep.id === 4 && (
              <Step4Review
                data={data}
                packages={packages}
                numLocale={numLocale}
                palette={palette}
                onEdit={goToStep}
                t={t}
              />
            )}
          </>
        )}
      </div>

      {!success && (
        <div style={{ flexShrink: 0 }}>
          <Footer
            stepIdx={stepIdx}
            isLast={stepIdx === STEPS.length - 1}
            canProceed={
              currentStep.id === 3 ? isStep3Valid : true
            }
            submitting={submitting}
            onBack={goBack}
            onNext={goNext}
            onSubmit={handleSubmit}
            t={t}
          />
        </div>
      )}
    </div>
  );
}

/* =====================================================================
 * Progress bar
 * ===================================================================== */

function ProgressBar({ steps, currentIdx, success, t }) {
  const activeIdx = success ? steps.length : currentIdx;
  return (
    <div className="d-flex flex-column gap-2">
      <div className="d-flex align-items-center justify-content-between font-12 fw-700">
        <span style={{ color: "hsl(var(--main))", letterSpacing: "0.06em" }}>
          {t("clientSetup.wizard.progress", {
            current: Math.min(activeIdx + 1, steps.length),
            total: steps.length,
          })}
        </span>
        <span style={{ color: "hsl(var(--body-color))" }}>
          {success
            ? t("clientSetup.wizard.steps." + steps[steps.length - 1].key)
            : t("clientSetup.wizard.steps." + steps[currentIdx].key)}
        </span>
      </div>
      <div className="d-flex gap-2">
        {steps.map((s, idx) => {
          const isActive = idx === currentIdx && !success;
          const isDone = idx < currentIdx || success;
          return (
            <div
              key={s.id}
              className="flex-grow-1 d-flex flex-column gap-1"
            >
              <div
                style={{
                  height: 6,
                  borderRadius: 99,
                  background:
                    isDone || isActive
                      ? "var(--main-gradient)"
                      : "hsl(var(--border-color))",
                  transition: "background 0.3s ease",
                }}
              />
              <div
                className="font-11 text-uppercase fw-700"
                style={{
                  color: isActive
                    ? "hsl(var(--main))"
                    : isDone
                    ? "hsl(var(--heading-color))"
                    : "hsl(var(--body-color))",
                  letterSpacing: "0.05em",
                  textAlign: "center",
                }}
              >
                {idx + 1}. {t("clientSetup.wizard.steps." + s.key)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =====================================================================
 * Step 1 — Referral attribution
 * ===================================================================== */

function Step1Referral({ data, onChange, palette, isVisuallyDark, t }) {
  const [validating, setValidating] = useState(false);
  const [validity, setValidity] = useState(null);
  const [code, setCode] = useState(
    data.source === "manual" ? data.code : "",
  );

  // Auto-validation when typing (fake — checks against directory)
  useEffect(() => {
    if (data.source === "link" || data.source === "saved") return;
    const trimmed = (code || "").trim().toUpperCase();
    if (!trimmed) {
      setValidity(null);
      return;
    }
    setValidating(true);
    const timer = setTimeout(() => {
      const partner = FAKE_PARTNER_DIRECTORY[trimmed];
      if (partner) {
        setValidity({ ok: true, partner });
        onChange({ code: trimmed, partner, source: "manual" });
      } else {
        setValidity({ ok: false });
        onChange({ code: "", partner: "", source: "none" });
      }
      setValidating(false);
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <div className="d-flex flex-column gap-3">
      <StepHeader
        title={t("clientSetup.wizard.step1.title")}
        subtitle={t("clientSetup.wizard.step1.subtitle")}
      />

      {data.source === "link" && (
        <AttributionDetected
          icon="la-link"
          msgKey="clientSetup.wizard.step1.viaLinkMsg"
          partner={data.partner}
          code={data.code}
          palette={palette}
          t={t}
        />
      )}

      {data.source === "saved" && (
        <AttributionDetected
          icon="la-bookmark"
          msgKey="clientSetup.wizard.step1.savedCodeMsg"
          partner={data.partner}
          code={data.code}
          palette={palette}
          t={t}
        />
      )}

      {data.source === "none" || data.source === "manual" ? (
        <div
          className="rounded-3 p-3"
          style={{
            backgroundColor: palette.surface.bg,
            border: palette.surface.border,
          }}
        >
          <label
            className="font-12 text-uppercase fw-700 mb-2 d-block"
            style={{
              letterSpacing: "0.05em",
              color: "hsl(var(--heading-color))",
            }}
          >
            {t("clientSetup.wizard.step1.manualLabel")}
          </label>
          <div className="position-relative">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t("clientSetup.attribution.addCodePlaceholder")}
              className="common-input border font-15 fw-600 text-uppercase"
              style={{ letterSpacing: "0.05em", paddingRight: 48 }}
              autoFocus
            />
            {validating && (
              <span
                className="position-absolute spinner-border spinner-border-sm"
                style={{
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "hsl(var(--main))",
                }}
                role="status"
                aria-hidden="true"
              />
            )}
            {validity?.ok && !validating && (
              <i
                className="las la-check-circle position-absolute"
                style={{
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "1.4rem",
                  color: palette.success.accentStrong,
                }}
                aria-hidden="true"
              />
            )}
          </div>
          <div className="font-12 mt-2">
            {validity?.ok ? (
              <span style={{ color: palette.success.accent }}>
                <i className="las la-check-circle me-1" />
                {t("clientSetup.attribution.valid", {
                  partner: validity.partner,
                })}
              </span>
            ) : validity?.ok === false ? (
              <span style={{ color: palette.danger.accent }}>
                <i className="las la-times-circle me-1" />
                {t("clientSetup.attribution.invalid")}
              </span>
            ) : (
              <span style={{ color: "hsl(var(--body-color))" }}>
                {t("clientSetup.wizard.step1.manualHint")}
              </span>
            )}
          </div>
          <div
            className="d-flex flex-wrap align-items-center gap-1 mt-2 font-11"
            style={{ color: "hsl(var(--body-color))" }}
          >
            <span className="me-1">Demo codes:</span>
            {Object.keys(FAKE_PARTNER_DIRECTORY).map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCode(c)}
                className="btn btn-sm rounded-pill px-2 py-0 font-11"
                style={{
                  backgroundColor: isVisuallyDark
                    ? "hsl(var(--main) / 0.22)"
                    : "hsl(var(--main) / 0.1)",
                  color: isVisuallyDark
                    ? "hsl(var(--main) / 1)"
                    : "hsl(var(--main))",
                  border: "none",
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function AttributionDetected({ icon, msgKey, partner, code, palette, t }) {
  return (
    <div
      className="rounded-3 p-3 d-flex align-items-start gap-3"
      style={{
        background: palette.success.bg,
        border: palette.success.border,
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
        style={{
          width: 44,
          height: 44,
          backgroundColor: palette.success.iconBg,
          color: palette.success.iconColor,
        }}
        aria-hidden="true"
      >
        <i className={`las ${icon}`} style={{ fontSize: "1.4rem" }} />
      </span>
      <div className="flex-grow-1">
        <div
          className="font-14"
          style={{ color: "hsl(var(--heading-color))" }}
          dangerouslySetInnerHTML={{
            __html: t(msgKey, { partner }),
          }}
        />
        <div
          className="font-12 mt-1"
          style={{
            color: palette.success.accent,
            letterSpacing: "0.04em",
            fontFamily: "monospace",
          }}
        >
          {code}
        </div>
      </div>
      <i
        className="las la-check-circle"
        style={{ fontSize: "1.6rem", color: palette.success.accentStrong }}
        aria-hidden="true"
      />
    </div>
  );
}

/* =====================================================================
 * Step 2 — Package selection
 * ===================================================================== */

function Step2Package({
  data,
  packages,
  recommendedKey,
  numLocale,
  palette,
  onChange,
  t,
}) {
  return (
    <div className="d-flex flex-column gap-3">
      <StepHeader
        title={t("clientSetup.wizard.step2.title")}
        subtitle={t("clientSetup.wizard.step2.subtitle")}
      />

      <div className="row gy-3">
        {packages.map((pkg) => {
          const selected = data === pkg.key;
          const recommended = recommendedKey === pkg.key;
          return (
            <div key={pkg.key} className="col-md-4">
              <button
                type="button"
                onClick={() => onChange(pkg.key)}
                className="w-100 text-start rounded-3 p-3 d-flex flex-column gap-2 position-relative"
                style={{
                  backgroundColor: selected
                    ? "hsl(var(--main) / 0.12)"
                    : palette.surface.bg,
                  border: selected
                    ? "2px solid hsl(var(--main))"
                    : palette.surface.border.replace("1px", "2px"),
                  color: "hsl(var(--heading-color))",
                  cursor: "pointer",
                  minHeight: 240,
                  transition: "all 0.2s ease",
                }}
              >
                {recommended && (
                  <span
                    className="position-absolute badge fw-600 rounded-pill px-3 py-1"
                    style={{
                      top: -10,
                      left: 12,
                      background: "var(--main-gradient)",
                      color: "#fff",
                      fontSize: 10,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      boxShadow: "0 4px 12px hsl(var(--main) / 0.3)",
                    }}
                  >
                    <i className="las la-star me-1" />
                    {t("clientSetup.wizard.step2.recommended")}
                  </span>
                )}

                <div className="font-18 fw-700 mt-1">{pkg.name}</div>
                <div>
                  <span
                    className="font-22 fw-700"
                    style={{ color: "hsl(var(--main))" }}
                  >
                    {new Intl.NumberFormat(numLocale).format(pkg.price)} ֏
                  </span>
                </div>

                <ul
                  className="list-unstyled mb-0 mt-2 d-flex flex-column gap-1 flex-grow-1"
                  style={{ paddingLeft: 0 }}
                >
                  {pkg.features.map((f, i) => (
                    <li
                      key={i}
                      className="font-13 d-flex align-items-start gap-2"
                      style={{ color: "hsl(var(--heading-color))" }}
                    >
                      <i
                        className="las la-check"
                        style={{
                          color: "hsl(var(--main))",
                          fontSize: "1rem",
                          marginTop: 3,
                        }}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <div
                  className="font-13 fw-600 mt-2 pt-2 d-flex align-items-center justify-content-between"
                  style={{
                    borderTop: "1px solid hsl(var(--border-color))",
                    color: selected ? "hsl(var(--main))" : "hsl(var(--body-color))",
                  }}
                >
                  {selected ? (
                    <>
                      <i className="las la-check-circle" />
                      <span>{t("clientSetup.wizard.step2.selected")}</span>
                    </>
                  ) : (
                    <>
                      <span>{t("clientSetup.wizard.step2.selectBtn")}</span>
                      <i className="las la-arrow-right" />
                    </>
                  )}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="btn btn-outline-main pill fw-500 align-self-start px-4"
        disabled
        title="Demo only"
      >
        <i className="las la-cube me-1" />
        {t("clientSetup.wizard.step2.customBtn")}
      </button>
    </div>
  );
}

/* =====================================================================
 * Step 3 — Organization & admin
 * ===================================================================== */

function Step3Organization({ data, onChange, palette, isVisuallyDark, t }) {
  const orgTypes = ["bank", "healthcare", "government", "other"];

  return (
    <div className="d-flex flex-column gap-3">
      <StepHeader
        title={t("clientSetup.wizard.step3.title")}
        subtitle={t("clientSetup.wizard.step3.subtitle")}
      />

      <div className="row gy-3">
        <div className="col-12">
          <Label text={t("clientSetup.wizard.step3.orgNameLabel")} required />
          <input
            type="text"
            className="common-input border"
            value={data.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder={t("clientSetup.wizard.step3.orgNamePlaceholder")}
          />
        </div>

        <div className="col-12">
          <Label text={t("clientSetup.wizard.step3.orgTypeLabel")} required />
          <div className="row gx-2 gy-2">
            {orgTypes.map((typeKey) => {
              const selected = data.type === typeKey;
              return (
                <div key={typeKey} className="col-6 col-md-3">
                  <button
                    type="button"
                    onClick={() => onChange({ type: typeKey })}
                    className="w-100 rounded-3 px-3 py-2 font-13 fw-500"
                    style={{
                      backgroundColor: selected
                        ? "hsl(var(--main) / 0.16)"
                        : palette.surfaceMuted,
                      border: selected
                        ? "1px solid hsl(var(--main))"
                        : "1px solid hsl(var(--border-color))",
                      color: selected
                        ? "hsl(var(--main))"
                        : "hsl(var(--heading-color))",
                      cursor: "pointer",
                    }}
                  >
                    {t(`clientSetup.wizard.step3.orgTypes.${typeKey}`)}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="mt-2 pt-3"
        style={{
          borderTop: "1px solid hsl(var(--border-color))",
        }}
      >
        <div
          className="font-13 text-uppercase fw-700 mb-3"
          style={{
            letterSpacing: "0.06em",
            color: "hsl(var(--main))",
          }}
        >
          <i className="las la-user-shield me-1" />
          {t("clientSetup.wizard.step3.adminSectionTitle")}
        </div>
        <div className="row gy-3">
          <div className="col-md-6">
            <Label text={t("clientSetup.wizard.step3.adminFirstNameLabel")} />
            <input
              type="text"
              className="common-input border"
              value={data.adminFirstName}
              onChange={(e) => onChange({ adminFirstName: e.target.value })}
            />
          </div>
          <div className="col-md-6">
            <Label text={t("clientSetup.wizard.step3.adminLastNameLabel")} />
            <input
              type="text"
              className="common-input border"
              value={data.adminLastName}
              onChange={(e) => onChange({ adminLastName: e.target.value })}
            />
          </div>
          <div className="col-12">
            <Label
              text={t("clientSetup.wizard.step3.adminUsernameLabel")}
              required
            />
            <input
              type="text"
              className="common-input border"
              value={data.adminUsername}
              onChange={(e) =>
                onChange({ adminUsername: e.target.value.toLowerCase() })
              }
              placeholder="ararat_admin"
              autoComplete="username"
            />
            <div className="font-12 text-body mt-1">
              <i className="las la-info-circle me-1" />
              {t("clientSetup.wizard.step3.adminUsernameHint")}
            </div>
          </div>
          <div className="col-md-6">
            <Label
              text={t("clientSetup.wizard.step3.passwordLabel")}
              required
            />
            <input
              type="password"
              className="common-input border"
              value={data.password}
              onChange={(e) => onChange({ password: e.target.value })}
              autoComplete="new-password"
            />
            <PasswordStrength value={data.password} isVisuallyDark={isVisuallyDark} />
          </div>
          <div className="col-md-6">
            <Label
              text={t("clientSetup.wizard.step3.passwordConfirmLabel")}
              required
            />
            <input
              type="password"
              className="common-input border"
              value={data.passwordConfirm}
              onChange={(e) =>
                onChange({ passwordConfirm: e.target.value })
              }
              autoComplete="new-password"
            />
            {data.passwordConfirm &&
              data.password !== data.passwordConfirm && (
                <div
                  className="font-12 mt-1"
                  style={{ color: palette.danger.accent }}
                >
                  <i className="las la-times-circle me-1" />
                  Գաղտնաբառերը չեն համընկնում
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordStrength({ value, isVisuallyDark }) {
  if (!value) return null;
  const hasLength = value.length >= 8;
  const hasDigit = /\d/.test(value);
  const hasUpper = /[A-ZԱ-Ֆ]/.test(value);
  const score = [hasLength, hasDigit, hasUpper].filter(Boolean).length;
  const labels = ["Թույլ", "Միջին", "Լավ", "Ուժեղ"];
  const colors = getPasswordStrengthColors(isVisuallyDark);
  return (
    <div className="d-flex align-items-center gap-2 mt-1">
      <div className="d-flex gap-1 flex-grow-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              height: 4,
              borderRadius: 2,
              flex: 1,
              backgroundColor:
                i < score
                  ? colors[score]
                  : "hsl(var(--border-color))",
              transition: "background 0.2s",
            }}
          />
        ))}
      </div>
      <span
        className="font-12 fw-600"
        style={{ color: colors[score], minWidth: 50, textAlign: "right" }}
      >
        {labels[score]}
      </span>
    </div>
  );
}

/* =====================================================================
 * Step 4 — Review & confirm
 * ===================================================================== */

function Step4Review({ data, packages, numLocale, palette, onEdit, t }) {
  const pkg = packages.find((p) => p.key === data.packageKey);
  return (
    <div className="d-flex flex-column gap-3">
      <StepHeader
        title={t("clientSetup.wizard.step4.title")}
        subtitle={t("clientSetup.wizard.step4.subtitle")}
      />

      <ReviewSection
        icon="la-link"
        title={t("clientSetup.wizard.step4.section.referral")}
        palette={palette}
        onEdit={() => onEdit(0)}
        editLabel={t("clientSetup.wizard.step4.edit")}
      >
        {data.referral.code ? (
          <div>
            <div
              className="fw-600"
              style={{ color: "hsl(var(--heading-color))" }}
            >
              {data.referral.partner}
            </div>
            <div
              className="font-12"
              style={{
                color: "hsl(var(--body-color))",
                fontFamily: "monospace",
                letterSpacing: "0.04em",
              }}
            >
              {data.referral.code}
            </div>
          </div>
        ) : (
          <span
            className="font-14 fst-italic"
            style={{ color: "hsl(var(--body-color))" }}
          >
            {t("clientSetup.wizard.step4.noReferral")}
          </span>
        )}
      </ReviewSection>

      <ReviewSection
        icon="la-cube"
        title={t("clientSetup.wizard.step4.section.package")}
        palette={palette}
        onEdit={() => onEdit(1)}
        editLabel={t("clientSetup.wizard.step4.edit")}
      >
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div
            className="fw-600"
            style={{ color: "hsl(var(--heading-color))" }}
          >
            {pkg.name}
          </div>
          <div
            className="fw-700"
            style={{ color: "hsl(var(--main))" }}
          >
            {new Intl.NumberFormat(numLocale).format(pkg.price)} ֏
          </div>
        </div>
      </ReviewSection>

      <ReviewSection
        icon="la-building"
        title={t("clientSetup.wizard.step4.section.organization")}
        palette={palette}
        onEdit={() => onEdit(2)}
        editLabel={t("clientSetup.wizard.step4.edit")}
      >
        <div
          className="fw-600"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          {data.organization.name}
        </div>
        <div
          className="font-13"
          style={{ color: "hsl(var(--body-color))" }}
        >
          {data.organization.type &&
            t(`clientSetup.wizard.step3.orgTypes.${data.organization.type}`)}
        </div>
      </ReviewSection>

      <ReviewSection
        icon="la-user-shield"
        title={t("clientSetup.wizard.step4.section.admin")}
        palette={palette}
        onEdit={() => onEdit(2)}
        editLabel={t("clientSetup.wizard.step4.edit")}
      >
        <div
          className="fw-600"
          style={{ color: "hsl(var(--heading-color))" }}
        >
          {data.organization.adminFirstName} {data.organization.adminLastName}
        </div>
        <div
          className="font-13"
          style={{
            color: "hsl(var(--body-color))",
            fontFamily: "monospace",
          }}
        >
          @{data.organization.adminUsername || "—"}
        </div>
      </ReviewSection>
    </div>
  );
}

function ReviewSection({ icon, title, palette, onEdit, editLabel, children }) {
  return (
    <div
      className="rounded-3 p-3 d-flex align-items-start gap-3"
      style={{
        backgroundColor: palette.surface.bg,
        border: palette.surface.border,
      }}
    >
      <span
        className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
        style={{
          width: 40,
          height: 40,
          backgroundColor: "hsl(var(--main) / 0.12)",
          color: "hsl(var(--main))",
        }}
        aria-hidden="true"
      >
        <i className={`las ${icon}`} style={{ fontSize: "1.3rem" }} />
      </span>
      <div className="flex-grow-1 min-width-0">
        <div
          className="font-11 text-uppercase fw-700 mb-1"
          style={{
            letterSpacing: "0.06em",
            color: "hsl(var(--body-color))",
          }}
        >
          {title}
        </div>
        {children}
      </div>
      <button
        type="button"
        onClick={onEdit}
        className="btn btn-sm rounded-pill px-3 fw-500 font-12"
        style={{
          backgroundColor: "transparent",
          color: "hsl(var(--main))",
          border: "1px solid hsl(var(--main) / 0.4)",
        }}
      >
        <i className="las la-pen me-1" />
        {editLabel}
      </button>
    </div>
  );
}

/* =====================================================================
 * Success screen
 * ===================================================================== */

function SuccessScreen({ secondsLeft, onClose, t }) {
  return (
    <div className="text-center py-4 d-flex flex-column align-items-center gap-3">
      <div
        className="d-inline-flex align-items-center justify-content-center text-white"
        style={{
          width: 88,
          height: 88,
          borderRadius: 24,
          background: "var(--main-gradient)",
          boxShadow: "0 12px 32px hsl(var(--main) / 0.4)",
        }}
        aria-hidden="true"
      >
        <i className="las la-check-circle" style={{ fontSize: "3rem" }} />
      </div>
      <h4
        className="font-22 fw-700 mb-0 mt-2"
        style={{ color: "hsl(var(--heading-color))" }}
      >
        {t("clientSetup.wizard.step4.successTitle")}
      </h4>
      <p className="font-14 text-body mb-0" style={{ maxWidth: 420 }}>
        {t("clientSetup.wizard.step4.successDescription", {
          seconds: secondsLeft,
        })}
      </p>
      <div className="d-flex gap-2 mt-2">
        <a
          href="https://dashboard.nomio.am/login"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-main pill fw-500 px-4"
        >
          <i className="las la-external-link-alt me-1" />
          {t("clientSetup.wizard.step4.redirectNow")}
        </a>
      </div>
    </div>
  );
}

/* =====================================================================
 * Footer (Back / Next / Submit)
 * ===================================================================== */

function Footer({
  stepIdx,
  isLast,
  canProceed,
  submitting,
  onBack,
  onNext,
  onSubmit,
  t,
}) {
  return (
    <div
      className="d-flex justify-content-between gap-2 pt-3"
      style={{
        borderTop: "1px solid hsl(var(--border-color))",
      }}
    >
      <button
        type="button"
        onClick={onBack}
        disabled={stepIdx === 0}
        className="btn btn-outline-main pill fw-500 px-3"
        style={{
          opacity: stepIdx === 0 ? 0.4 : 1,
        }}
      >
        <i className="las la-arrow-left me-1" />
        {t("clientSetup.wizard.back")}
      </button>

      {isLast ? (
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitting}
          className="btn btn-main pill fw-500 px-4"
        >
          {submitting ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              {t("clientSetup.wizard.submitting")}
            </>
          ) : (
            <>
              <i className="las la-rocket me-1" />
              {t("clientSetup.wizard.submit")}
            </>
          )}
        </button>
      ) : (
        <button
          type="button"
          onClick={onNext}
          disabled={!canProceed}
          className="btn btn-main pill fw-500 px-4"
          style={{ opacity: canProceed ? 1 : 0.6 }}
        >
          {t("clientSetup.wizard.next")}
          <i className="las la-arrow-right ms-1" />
        </button>
      )}
    </div>
  );
}

/* =====================================================================
 * Tiny building blocks
 * ===================================================================== */

function StepHeader({ title, subtitle }) {
  return (
    <div>
      <h4
        className="font-22 fw-700 mb-1"
        style={{ color: "hsl(var(--heading-color))" }}
      >
        {title}
      </h4>
      <p
        className="font-14 mb-0"
        style={{ color: "hsl(var(--body-color))" }}
      >
        {subtitle}
      </p>
    </div>
  );
}

function Label({ text, required }) {
  return (
    <label
      className="font-13 fw-600 mb-1 d-block"
      style={{ color: "hsl(var(--heading-color))" }}
    >
      {text}
      {required && (
        <span style={{ color: "hsl(0 75% 60%)" }}> *</span>
      )}
    </label>
  );
}

export default ClientPlatformSetupWizard;
